import { useCallback } from 'react';

import { type BaseCurrencyType } from '@pkg/types';
import { convertOctToAptos, convertWeiToEth } from '@pkg/utils';

import { percentageDiff } from '@app/pages/ProfilePage/utils';
import { getFormattedCurrency } from '@app/store/zustand/base-currency/useBaseCurrencyStore';
import { getConversion } from '@app/store/zustand/exchange-rates/useExchangeRatesStore';

interface PricingDataArgs {
    weiValue: string; // or AptOct if Aptos
    currency: BaseCurrencyType;
}
interface PricingData {
    nativeValue: number;
    usdValue: number;
}

interface FormattedPricingData {
    nativeValue: string;
    usdValue: string;
}

interface PriceComparisons {
    weiValue: string; // or AptOct if Aptos
    floorPriceWei: string; // or AptOct if Aptos
    lastSale?: string;
    listPrice?: string;
    offerPrice?: string;
}

export function usePricing() {
    const getPricingValues = useCallback(({ weiValue, currency }: PricingDataArgs): PricingData => {
        if (currency === 'apt') {
            const nativeValue = convertOctToAptos(parseFloat(weiValue));
            const usdValue = getConversion('apt', 'usd', nativeValue);
            return { usdValue, nativeValue };
        }
        const valueFromWei = convertWeiToEth(parseFloat(weiValue));
        const usdValue = getConversion(currency, 'usd', valueFromWei);
        return { usdValue, nativeValue: valueFromWei };
    }, []);

    const getFormattedPricingValues = useCallback(
        ({ weiValue, currency }: PricingDataArgs): FormattedPricingData => {
            const { usdValue, nativeValue } = getPricingValues({ weiValue, currency });
            return {
                usdValue: getFormattedCurrency('usd', usdValue, true),
                nativeValue: getFormattedCurrency(currency, nativeValue, true),
            };
        },
        [getPricingValues],
    );

    const getComparisons = useCallback(
        ({ weiValue, floorPriceWei, offerPrice, listPrice, lastSale }: PriceComparisons) => {
            const floorPriceDiff = percentageDiff(parseFloat(floorPriceWei), parseFloat(weiValue));
            // TODO: other comparisons
            return { floorPriceDiff, offerPrice, listPrice, lastSale };
        },
        [],
    );

    return { getComparisons, getPricingValues, getFormattedPricingValues };
}
